import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { ExternalLink, Pencil, Plus, Wine as WineIcon } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, EmptyState } from "@/components/admin/AdminPageHeader"
import { FilterBar } from "@/components/admin/FilterBar"
import { plural } from "@/components/admin/format"
import { ResponsiveList } from "@/components/admin/ResponsiveList"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { formatPenceOr } from "@/lib/money"
import { cn } from "@/lib/utils"
import type { AdminWineRow, AdminWinesIndexProps, PageProps } from "@/types/inertia"

const TITLE = "Wines"
const DESCRIPTION =
  "The Sideways catalogue: search and filter, choose which wines are carried and featured, and open any wine to edit its copy, price and image."

type Flag = "carried" | "featured"

export default function AdminWinesIndex() {
  const { props } = usePage<PageProps<AdminWinesIndexProps>>()
  const { wines, filters, wine_types: wineTypes, counts } = props
  // The wine whose switch is on its way to the server, so it cannot be
  // pressed twice.
  const [pending, setPending] = React.useState<number | null>(null)

  const toggle = (wine: AdminWineRow, flag: Flag) => {
    router.patch(
      wine.flags_url,
      { [flag]: !wine[flag] },
      {
        preserveScroll: true,
        preserveState: true,
        onStart: () => setPending(wine.id),
        onFinish: () => setPending(null),
      },
    )
  }

  const filtered = Boolean(filters.q || filters.wine_type || filters.carried)

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title="Wines"
          description={`${counts.carried.toLocaleString("en-GB")} carried of ${plural(counts.total, "wine")}; ${counts.featured.toLocaleString("en-GB")} featured on the home page.`}
          actions={
            <Button asChild size="lg">
              <Link href="/admin/wines/new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                Add a wine
              </Link>
            </Button>
          }
        />

        <FilterBar
          path="/admin/wines"
          idPrefix="wines-filter"
          values={filters}
          fields={[
            { kind: "search", name: "q", label: "Search", placeholder: "Name or producer" },
            { kind: "select", name: "wine_type", label: "Type", options: wineTypes, allLabel: "All types" },
            {
              kind: "select",
              name: "carried",
              label: "Carried",
              allLabel: "Carried or not",
              options: [
                { value: "yes", label: "Carried" },
                { value: "no", label: "Not carried" },
              ],
            },
          ]}
        />

        <p className="mt-6" aria-live="polite">
          {filtered
            ? `${plural(wines.length, "wine")} ${wines.length === 1 ? "matches" : "match"}.`
            : `${plural(wines.length, "wine")}, carried first.`}
        </p>

        <div className="mt-3">
          {wines.length === 0 ? (
            <EmptyState>No wines match these filters.</EmptyState>
          ) : (
            <ResponsiveList
              label="Wines"
              items={wines}
              getKey={(wine) => wine.id}
              card={(wine) => <WineCard wine={wine} pending={pending === wine.id} onToggle={toggle} />}
              columns={[
                {
                  key: "image",
                  header: "Image",
                  srOnlyHeader: true,
                  className: "w-16",
                  cell: (wine) => <WineThumb wine={wine} />,
                },
                {
                  key: "wine",
                  header: "Wine",
                  cell: (wine) => (
                    <div className="min-w-0">
                      <div className="font-medium">
                        <Link href={wine.edit_url}>{wine.name}</Link>
                      </div>
                      <div className="text-xs text-ink-muted">
                        {wine.producer} · {wine.wine_type_label}
                        {wine.taste_label && ` · ${wine.taste_label}`}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "price",
                  header: "Price",
                  className: "whitespace-nowrap tabular-nums",
                  cell: (wine) => formatPenceOr(wine.uk_price_pence, "No price"),
                },
                {
                  key: "carried",
                  header: "Carried",
                  cell: (wine) => (
                    <FlagSwitch wine={wine} flag="carried" pending={pending === wine.id} onToggle={toggle} />
                  ),
                },
                {
                  key: "featured",
                  header: "Featured",
                  cell: (wine) => (
                    <FlagSwitch wine={wine} flag="featured" pending={pending === wine.id} onToggle={toggle} />
                  ),
                },
                {
                  key: "actions",
                  header: "Actions",
                  srOnlyHeader: true,
                  className: "text-right",
                  cell: (wine) => <WineActions wine={wine} />,
                },
              ]}
            />
          )}
        </div>
      </AdminShell>
    </>
  )
}

function WineThumb({ wine }: { wine: AdminWineRow }) {
  return (
    <div className="flex h-16 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md bg-surface">
      {wine.image_url ? (
        <img
          src={wine.image_url}
          alt=""
          loading="lazy"
          decoding="async"
          width={48}
          height={64}
          className="h-full w-full object-contain"
        />
      ) : (
        <WineIcon className="h-5 w-5 text-ink-muted" aria-hidden="true" />
      )}
    </div>
  )
}

/** A carried / featured switch: sends only this flag, keeps the scroll. */
function FlagSwitch({
  wine,
  flag,
  pending,
  onToggle,
  withLabel = false,
}: {
  wine: AdminWineRow
  flag: Flag
  pending: boolean
  onToggle: (wine: AdminWineRow, flag: Flag) => void
  withLabel?: boolean
}) {
  const label = flag === "carried" ? "Carried" : "Featured"
  return (
    <label className="inline-flex min-h-11 min-w-11 cursor-pointer items-center gap-2 text-sm font-normal text-ink-body">
      <Checkbox
        checked={wine[flag]}
        disabled={pending}
        onChange={() => onToggle(wine, flag)}
        aria-label={withLabel ? undefined : `${label}: ${wine.name}`}
      />
      {withLabel && label}
    </label>
  )
}

function WineActions({ wine, align = "end" }: { wine: AdminWineRow; align?: "start" | "end" }) {
  return (
    // Wraps below 1280px so the table fits the tablet width (M7 W3 responsiveness pass).
    <div className={cn("flex flex-wrap items-center gap-1 xl:flex-nowrap", align === "end" ? "justify-end" : "justify-start")}>
      {wine.public_url && (
        <Button asChild variant="ghost" size="icon-lg">
          <a href={wine.public_url} target="_blank" rel="noopener noreferrer" aria-label={`View ${wine.name} on the site`}>
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </Button>
      )}
      <Button asChild variant="secondary" size="lg">
        <Link href={wine.edit_url}>
          <Pencil className="h-4 w-4" aria-hidden="true" />
          Edit<span className="sr-only"> {wine.name}</span>
        </Link>
      </Button>
    </div>
  )
}

function WineCard({
  wine,
  pending,
  onToggle,
}: {
  wine: AdminWineRow
  pending: boolean
  onToggle: (wine: AdminWineRow, flag: Flag) => void
}) {
  return (
    <div className="flex gap-3 px-4 py-3">
      <WineThumb wine={wine} />
      <div className="min-w-0 flex-1">
        {/* Text, not a link: the 44px Edit button below is the card's way in. */}
        <div className="text-sm font-medium text-ink-display">{wine.name}</div>
        <div className="text-xs text-ink-muted">
          {wine.producer} · {wine.wine_type_label}
          {wine.taste_label && ` · ${wine.taste_label}`}
        </div>
        <div className="mt-1 text-sm tabular-nums text-ink-body">{formatPenceOr(wine.uk_price_pence, "No price")}</div>
        <div className="mt-1 flex flex-wrap items-center gap-x-5">
          <FlagSwitch wine={wine} flag="carried" pending={pending} onToggle={onToggle} withLabel />
          <FlagSwitch wine={wine} flag="featured" pending={pending} onToggle={onToggle} withLabel />
        </div>
        <div className="mt-1">
          <WineActions wine={wine} align="start" />
        </div>
      </div>
    </div>
  )
}
